import { Component, Input, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { StateAdditionalInformation, StateBodyCorporate } from '../../../models/body-corporate.model';
import { StateIndividual } from '../../../models/individual.model';
import { Roles, ROLES_MESSAGE_MAP } from '../../../../core/constants/roles.enum';
import { BodyCorporateModalComponent } from '../../modals/body-corporate-modal/body-corporate-modal.component';
import { IndividualModalComponent } from '../../modals/individual-modal/individual-modal.component';

@Component({
    selector: 'app-corporate-structure-section',
    templateUrl: './corporate-structure-section.component.html',
    styleUrls: ['./corporate-structure-section.component.scss'],
    standalone: false
})
export class CorporateStructureSectionComponent implements OnInit {
  @Input() corporateStructureAdditionalInfo?: StateAdditionalInformation;
  @Input() individuals?: StateIndividual[];
  @Input() bodyCorporates?: StateBodyCorporate[];
  @Input() isEntity: boolean = false;

  excludedRoles: Roles[] = [Roles.PAYR, Roles.PCNT, Roles.RCPT];
  additionalInfoQuestions: { question: string, value: boolean | null }[] = [];

  filteredIndividuals: StateIndividual[] = [];
  filteredBodyCorporates: StateBodyCorporate[] = [];
  
  constructor(private modal: NzModalService) {

  }

  ngOnInit(): void {
    this.setAdditionalInfoQuestions();
    this.filteredIndividuals = this.filterByVisibleRoles(this.individuals);
    this.filteredBodyCorporates = this.filterByVisibleRoles(this.bodyCorporates);
  }

  /**
   * Hides entries whose only roles are payer/contact/recipient (PAYR/PCNT/RCPT);
   * anyone carrying at least one other role stays visible.
   */
  private filterByVisibleRoles<T extends { jobTitles?: { titleCode?: string }[] }>(list?: T[]): T[] {
    const hiddenOnlyRoles = [Roles.PAYR, Roles.PCNT, Roles.RCPT];

    return (list ?? []).filter(item => {
      const titles = item.jobTitles ?? [];
      const hasOtherRole = titles.some(j => !hiddenOnlyRoles.includes(j.titleCode as Roles));
      if (hasOtherRole) return true;

      const hasHiddenRole = titles.some(j => hiddenOnlyRoles.includes(j.titleCode as Roles));
      return !hasHiddenRole;
    });
  }



  mapRoleToText(role: string): string {
    return ROLES_MESSAGE_MAP.get(role) || role;
  }

  checkRelevantPerson(item: string): boolean {
    return this.corporateStructureAdditionalInfo?.relevantPerson?.some((person) => person === item)!;
  }

  checkPersinalPartnershipControl(item: string): boolean {
    return this.corporateStructureAdditionalInfo?.naturalPersonPartnershipControl?.some((person) => person === item)!;
  }

  showIndividualModal(ind: StateIndividual) {
    this.modal.create({
      nzContent: IndividualModalComponent,
      nzData: {individual: ind, isPayer: false, isRecipient: false, isEntity: this.isEntity},
      nzFooter: null
    });
  }

  showBodyCorpModal(bc: StateBodyCorporate) {
    this.modal.create({
      nzContent: BodyCorporateModalComponent,
      nzData: {bodycorp: bc, isPayer: false, isRecipient: false, isEntity: this.isEntity},
      nzFooter: null,
      nzWidth: 900
    });
  }

  getFilteredJobTitlesIndividual(individual: StateIndividual): Roles[] {
    return individual.jobTitles.map(role => role.titleCode as Roles).filter(role => !this.excludedRoles.includes(role));
  }

  getFilteredJobTitlesBodyCorporated(bodyCorp: StateBodyCorporate): Roles[] {
    return bodyCorp.jobTitles.map(role => role.titleCode as Roles).filter(role => !this.excludedRoles.includes(role));
  }

  setAdditionalInfoQuestions() {
    this.additionalInfoQuestions = [];
    if (this.corporateStructureAdditionalInfo?.electionKeepMembersInfo != null) {
      this.additionalInfoQuestions.push({ question: 'Do you wish to make an election to keep information, which otherwise would require to be kept in the Register of Members, to be kept by the Registrar?', value: this.corporateStructureAdditionalInfo?.electionKeepMembersInfo });
    }
    if (this.corporateStructureAdditionalInfo?.electionKeepPartnersInfo != null) {
      this.additionalInfoQuestions.push({ question: 'Do you wish to make an election to keep information, which otherwise would require to be kept in the Register of Partners, to be kept by the Registrar?', value: this.corporateStructureAdditionalInfo?.electionKeepPartnersInfo });
    }
    if (this.corporateStructureAdditionalInfo?.proposedSubsidary != null) {
      this.additionalInfoQuestions.push({ question: 'Is a legal entity proposed to be a subsidiary?', value: this.corporateStructureAdditionalInfo?.proposedSubsidary });
    }

    let showQuestion: boolean = false;
    let question = {
      question: 'Please confirm whether one of the following statements is applicable (Please note that any information may be required at the AFSA Registrar of Companies` discretion?)',
      value: null
    }
    question.question += '<ul>';
    if (this.checkRelevantPerson('RPSecListedOnRecogExch') || this.checkPersinalPartnershipControl('tradedOnExchange')) {
      question.question += '<li>the Relevant Person has its Securities listed or traded on a Recognised Exchange</li>';
      showQuestion = true;
    }
    if (this.checkRelevantPerson('RpOwnedByGovOfRelJur')) {
      question.question += '<li>the Relevant Person is wholly owned by a government or government agency of a Relevant Jurisdiction</li>';
      showQuestion = true;
    }
    if (this.checkRelevantPerson('RPEstablishedUndKzLow') || this.checkPersinalPartnershipControl('underLowOfKz')) {
      question.question += '<li>the Relevant Person is established under a law of Kazakhstan to perform governmental functions</li>';
      showQuestion = true;
    }
    if ( this.checkRelevantPerson('NA')) {
      question.question += '<li>No applicable</li>';
      showQuestion = true;
    }
    if (showQuestion) {
      question.question += '</ul>';
      this.additionalInfoQuestions.push(question);
    }



    if (this.corporateStructureAdditionalInfo?.naturalPersonControl != null) {
      this.additionalInfoQuestions.push({ question: 'Is there any natural person who: <br> <ul> <li> (i) owns or controls (directly or indirectly) Shares in the share capital of the company or other Ownership Interests in the Relevant Person of at least 25%; </li> <li> (ii) owns or controls (directly or indirectly) voting rights in the Relevant Person of at least 25%; </li> <li> (iii) owns or controls (directly or indirectly) the right to appoint or remove the majority of the Directors of the Relevant Person; </li> <li> (iv) has the legal right or through other ownership interests to exercise or actually exercises, significant control or influence over the activities of the company? </li> </ul>', value: this.corporateStructureAdditionalInfo?.naturalPersonControl });
    }
    if (this.corporateStructureAdditionalInfo?.naturalPersonExerciseControl != null) {
      this.additionalInfoQuestions.push({ question: 'Is there any natural person who has the legal right or through other ownership interests to exercise or actually exercises, significant control or influence over the activities of the company?', value: this.corporateStructureAdditionalInfo?.naturalPersonExerciseControl });
    }
    if (this.corporateStructureAdditionalInfo?.naturalPersonNPIOControl != null) {
      this.additionalInfoQuestions.push({ question: 'Is there any natural person who has the legal right to exercise, or actually exercises, significant control or influence over the activities of the Governing Body, Person or other arrangement administering the property of carrying out the objects of the NPIO?', value: this.corporateStructureAdditionalInfo?.naturalPersonNPIOControl });
    }
    if (this.corporateStructureAdditionalInfo?.naturalPersonFoundationControl != null) {
      this.additionalInfoQuestions.push({ question: 'Is there any natural person who has the legal right to exercise, or actually exercises, significant control or influence over the activities of the Governing Body, Person or other arrangement administering the property of carrying out the objects of the Foundation?', value: this.corporateStructureAdditionalInfo?.naturalPersonFoundationControl });
    }
  }

}
